import type React from "react";
import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode, TextareaHTMLAttributes } from "react";

export function Button({ children, className = "", variant = "primary", ...props }: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: "primary" | "secondary" | "danger" | "ghost" }) {
  const base = "inline-flex items-center px-3 py-1.5 rounded text-sm font-medium transition disabled:opacity-50 disabled:cursor-not-allowed";
  const styles = {
    primary: "bg-stone-900 text-white hover:bg-stone-700",
    secondary: "bg-stone-200 text-stone-900 hover:bg-stone-300",
    danger: "bg-red-600 text-white hover:bg-red-700",
    ghost: "text-stone-700 hover:bg-stone-200",
  }[variant];
  return <button {...props} className={`${base} ${styles} ${className}`}>{children}</button>;
}

export function Input({ className = "", ...props }: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...props}
      className={`block w-full rounded border border-stone-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-stone-400 ${className}`}
    />
  );
}

export function Textarea({ className = "", ...props }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea
      {...props}
      className={`block w-full rounded border border-stone-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-stone-400 ${className}`}
    />
  );
}

export function Card({ children, className = "", ...rest }: React.HTMLAttributes<HTMLDivElement> & { children: ReactNode }) {
  return <div {...rest} className={`rounded border border-stone-200 bg-white p-4 shadow-sm ${className}`}>{children}</div>;
}

export function Badge({ children, tone = "neutral" }: { children: ReactNode; tone?: "neutral" | "green" | "red" | "yellow" | "blue" }) {
  const styles = {
    neutral: "bg-stone-200 text-stone-700",
    green: "bg-green-100 text-green-800",
    red: "bg-red-100 text-red-800",
    yellow: "bg-yellow-100 text-yellow-800",
    blue: "bg-blue-100 text-blue-800",
  }[tone];
  return <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${styles}`}>{children}</span>;
}

export function Label({ children, className = "" }: { children: ReactNode; className?: string }) {
  return <label className={`block text-xs font-medium text-stone-700 mb-1 ${className}`}>{children}</label>;
}

export function PageHeader({ title, actions }: { title: string; actions?: ReactNode }) {
  return (
    <div className="flex items-center justify-between mb-4">
      <h1 className="text-2xl font-semibold text-stone-900">{title}</h1>
      {actions && <div className="flex gap-2">{actions}</div>}
    </div>
  );
}

export function Table<T>({ rows, columns, onRowClick }: {
  rows: T[];
  columns: Array<{ header: string; cell: (r: T) => ReactNode; className?: string }>;
  onRowClick?: (r: T) => void;
}) {
  return (
    <div className="rounded border border-stone-200 bg-white overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead className="bg-stone-50 text-stone-700">
          <tr>
            {columns.map((c, i) => (
              <th key={i} className="px-3 py-2 text-left font-medium">{c.header}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr><td colSpan={columns.length} className="px-3 py-6 text-center text-stone-500">No rows</td></tr>
          )}
          {rows.map((r, ri) => (
            <tr
              key={ri}
              className={`border-t border-stone-100 ${onRowClick ? "cursor-pointer hover:bg-stone-50" : ""}`}
              onClick={onRowClick ? () => onRowClick(r) : undefined}
            >
              {columns.map((c, ci) => (
                <td key={ci} className={`px-3 py-2 ${c.className ?? ""}`}>{c.cell(r)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function fmtNum(n: number | null | undefined): string {
  if (n == null) return "—";
  return n.toLocaleString();
}

export function fmtTime(unixSeconds: number | null | undefined): string {
  if (!unixSeconds) return "—";
  const d = new Date(unixSeconds * 1000);
  return d.toISOString().replace("T", " ").slice(0, 16);
}
